<template>
  <ui-dropdown
    v-if="showDropdown"
    class="mr-3"
    :mode="UiDropdownTypes.Modes.Single"
    :model-value="[]"
    :options="assignmentTypeOptions"
    prop-to-check="id"
    label="name"
    placement="top"
    close-on-select
    @update:model-value="handleAssignmentTypeSelect"
  >
    <template #option="{ option }">
      <tippy
        v-if="option.disabled && option.id === 'reviewer-qa'"
        :content="$t('conversations.filters.form.reviewer_qa.disabled_option_tooltip')"
        placement="top"
      >
        <div class="cursor-not-allowed">
          <span class="pointer-events-none text-gray-50">{{ option.name }}</span>
        </div>
      </tippy>
      <span v-else-if="option.disabled" class="pointer-events-none cursor-not-allowed text-gray-50">{{
        option.name
      }}</span>
      <span v-else>{{ option.name }}</span>
    </template>
    <template #trigger>
      <ui-menu-base-trigger>
        <template #default="{ openMenu }">
          <ui-button secondary @click="openMenu">
            <span class="flex items-center gap-2">
              {{ $t('conversations.filters.form.convert') }}
              <chevron-down-icon class="text-gray-50 icon-m" />
            </span>
          </ui-button>
        </template>
      </ui-menu-base-trigger>
    </template>
  </ui-dropdown>
  <ui-button v-else secondary class="mr-3" @click="convertToAssignment('agent-qa')">
    {{ $t('conversations.filters.form.convert') }}
  </ui-button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { Tippy } from 'vue-tippy'
import { ChevronDownIcon } from '@klausapp/ui-kit/icons'
import { UiButton, UiDropdownV2 as UiDropdown, UiDropdownTypes, UiMenuBaseTrigger } from '@klausapp/ui-kit'
import { session } from '@/composables/useSession'
import i18n from '@/i18n'
import { trackEvent } from '@/tracking'
import { APPLICATIONS_EVENT_NAMES, APPLICATIONS_SUB_FEATURE_IDS } from '@/tracking/features/applications'

const props = defineProps<{
  filterName: string
  filterOptions: any
}>()

const emit = defineEmits<{
  close: []
}>()

const router = useRouter()

const showDropdown = computed(() => session.features.gradeTheReviewerDev)

// Check if review status filter exists and if it's NOT set to "reviewed"
const isNotReviewedStatus = computed(() => {
  if (!props.filterOptions || !Array.isArray(props.filterOptions)) return false

  const reviewStatusFilter = props.filterOptions.find((opt: any) => {
    const option = opt.option?.name || opt.option
    return option === 'RATING_STATUS'
  })

  if (!reviewStatusFilter) return false // If no review status filter, allow Reviewer QA

  const condition = reviewStatusFilter.condition?.name || reviewStatusFilter.condition

  // Check if the condition is NOT "reviewed" - disable if anything other than reviewed
  return condition !== 'RATING_RATED'
})

const assignmentTypeOptions = computed(() => [
  { id: 'agent-qa', name: i18n.t('settings.workspaces.assignments.agent_qa.title'), disabled: false },
  {
    id: 'reviewer-qa',
    name: i18n.t('settings.workspaces.assignments.reviewer_qa.title'),
    disabled: isNotReviewedStatus.value,
  },
])

const handleAssignmentTypeSelect = (selected: any) => {
  const selectedOption = Array.isArray(selected) ? selected[0] : selected
  const assignmentType = typeof selectedOption === 'string' ? selectedOption : selectedOption?.id

  // Check if the option is disabled
  const option = assignmentTypeOptions.value.find((opt) => opt.id === assignmentType)
  if (option?.disabled) return

  convertToAssignment(assignmentType)
}

const convertToAssignment = (assignmentType: string) => {
  trackEvent(APPLICATIONS_EVENT_NAMES.FILTER_TURN_INTO_ASSIGNMENT, APPLICATIONS_SUB_FEATURE_IDS.FILTERS, {
    entityName: assignmentType,
  })
  emit('close')
  const routeName =
    assignmentType === 'reviewer-qa' ? 'workspace.assignments.reviewer-qa.new' : 'workspace.assignments.agent-qa.new'
  router.push({
    name: routeName,
    params: { id: session.workspace.id },
    query: { name: props.filterName || '', conditions: encodeURIComponent(JSON.stringify(props.filterOptions)) },
  })
}
</script>
